import { useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash';
import {
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import { useDeleteShopClusterMutation, useEditShopClusterMutation, useGetClusterQuery } from 'core/API/clusterApi';
import { TShopClusterViewer } from './ShopClusterContainer';
import RSelect from 'components/atoms/formInputs/RSelect';
import { TClusterPayload } from 'pages/ClusterManagement/types';
import { getRandomHexColor } from 'utils/colors';
import ColorPicker from 'pages/Preferences/components/molecules/ColorsTab/ColorPicker';
import ClusterCard from '../../ClusterCard';

type TShopClusterAddModal = {
    isOpen: boolean;
    onClose: () => void;
    actionModalType: TShopClusterViewer['actionModalType'];
    setActionModalType: TShopClusterViewer['setActionModalType'];
    currentShop?: TShopClusterViewer['currentShop'];
};

const ShopClusterActionModal = ({
    isOpen,
    onClose,
    actionModalType,
    setActionModalType,
    currentShop,
}: TShopClusterAddModal) => {
    const translate = useTranslation();

    const [selected, setSelected] = useState<(string | number)[]>([]);
    const { data: cData, isError, isFetching } = useGetClusterQuery();

    const [newClusters, setNewClusters] = useState<TClusterPayload[]>([]);
    const [currentCluster, setCurrentCluster] = useState<string | null>(null);

    const [editShopClusterMutation, { isLoading: isEditLoading }] = useEditShopClusterMutation();
    const [deleteShopClusterMutation, { isLoading: isDeleteLoading }] = useDeleteShopClusterMutation();

    const isDelete = actionModalType === 'delete';

    const clusterData = useMemo(() => cData?.data || [], [cData]);
    const dataByLabel = useMemo(() => keyBy(clusterData, 'label'), [clusterData]);

    const newClustersByLabel = keyBy(newClusters, 'label');
    const clusterDataByLabel = keyBy(clusterData, 'label');

    const handleSubmit = async () => {
        currentShop?.shop_id &&
            (await editShopClusterMutation(
                selected.map((s) => ({
                    label: `${s}`,
                    shopId: currentShop.shop_id,
                    exist: !newClustersByLabel[s],
                    color: newClustersByLabel[s]?.color,
                })),
            ));

        setSelected([]);
        setCurrentCluster(null);

        onClose();
    };

    const handleDelete = async () => {
        currentShop?.shop_id && (await deleteShopClusterMutation(currentShop?.shop_id));

        setSelected([]);
        setCurrentCluster(null);

        onClose();
    };

    useEffect(() => {
        setSelected(currentShop?.clusters.map((c) => c.label) || []);
        setCurrentCluster(null);
    }, [currentShop]);

    useEffect(() => {
        const nClusters: TClusterPayload[] = [];
        const newClusterByLabel = keyBy(newClusters, 'label');

        selected.forEach((s) => {
            if (!dataByLabel[s]) {
                nClusters.push({
                    label: s.toString(),
                    color: newClusterByLabel[s.toString()]?.color || getRandomHexColor(),
                });
            }
        });

        setNewClusters(nClusters);
    }, [selected]);

    const setColor = (color: string, label: string) => {
        setNewClusters((c) => {
            const newC = [...c];
            const cToUpdate = newC.find((c) => c.label === label) || { color: '' };
            cToUpdate.color = color;
            return newC;
        });
    };

    const options =
        clusterData.map((cluster) => ({
            label: cluster.label,
            value: cluster.label,
            color: cluster.color,
        })) || [];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate(`shopCluster.${actionModalType}Modal.header`)}</ModalHeader>
                <ModalBody>
                    {isDelete ? (
                        <Text>{translate('shopCluster.delete.confirm')}</Text>
                    ) : (
                        <>
                            <Text>
                                {translate('shopCluster.shopId')} : {currentShop?.shop_id}
                            </Text>
                            <Text>
                                {translate('shopCluster.shopName')} : {currentShop?.name}
                            </Text>
                            <RSelect options={options} onChange={setSelected} selectedOptions={selected} isCreatable />
                            <Text>{translate('shopCluster.newClusters')}</Text>
                            <Flex direction="row" gap={2} h="320px">
                                <Flex
                                    gap={3}
                                    flexDirection="column"
                                    mt={2}
                                    maxH="100%"
                                    overflow="auto"
                                    p={1}
                                    flexGrow={1}
                                >
                                    {selected.map((key, idx) => (
                                        <Box
                                            onClick={() =>
                                                !!newClustersByLabel[key] &&
                                                setCurrentCluster(currentCluster === `${key}` ? null : `${key}`)
                                            }
                                            cursor={!!newClustersByLabel[key] ? 'pointer' : 'default'}
                                        >
                                            <ClusterCard
                                                cluster={clusterDataByLabel[key] || newClustersByLabel[key]}
                                                isNew={!!newClustersByLabel[key]}
                                                selected={currentCluster === `${key}`}
                                            />
                                        </Box>
                                    ))}
                                </Flex>
                                <Flex justifyContent="center">
                                    {newClustersByLabel[`${currentCluster}`] && (
                                        <ColorPicker
                                            colorToSubmit={{
                                                color: newClustersByLabel[`${currentCluster}`]?.color || '',
                                            }}
                                            changeColor={(c: string) => currentCluster && setColor(c, currentCluster)}
                                        />
                                    )}
                                </Flex>
                            </Flex>
                        </>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    {!isDelete && (
                        <Button colorScheme="red" onClick={() => setActionModalType('delete')}>
                            {translate('shopCluster.delete')}
                        </Button>
                    )}
                    <Box flexGrow={1} />
                    <Button colorScheme="gray" onClick={onClose}>
                        {translate('shopCluster.cancel')}
                    </Button>
                    <Button colorScheme="blue" onClick={isDelete ? handleDelete : handleSubmit}>
                        {translate(`shopCluster.${actionModalType}Modal.onSubmit`)}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ShopClusterActionModal;
